<template>
  <v-container fluid>
    <v-row class="mb-2" align="center">
      <v-col cols="auto" class="text-h6">Verkäufe</v-col>
      <v-spacer />
      <v-col cols="auto">
        <v-btn small text @click="reload">
          <v-icon left small>mdi-refresh</v-icon>
          Aktualisieren
        </v-btn>
      </v-col>
    </v-row>

    <v-row class="mb-2" dense>
      <v-col cols="6" sm="4" md="2">
        <v-card outlined class="pa-3">
          <div class="text-caption text--secondary">Verkäufe</div>
          <div class="text-h6">{{ summary.count }}</div>
        </v-card>
      </v-col>
      <v-col cols="6" sm="4" md="2">
        <v-card outlined class="pa-3">
          <div class="text-caption text--secondary">Erlös gesamt</div>
          <div class="text-h6">{{ fmtEur(summary.proceeds) }}</div>
        </v-card>
      </v-col>
      <v-col cols="6" sm="4" md="2">
        <v-card outlined class="pa-3">
          <div class="text-caption text--secondary">Anschaffung gesamt</div>
          <div class="text-h6">{{ fmtEur(summary.cost) }}</div>
        </v-card>
      </v-col>
      <v-col cols="6" sm="4" md="2">
        <v-card outlined class="pa-3">
          <div class="text-caption text--secondary">Dividenden gesamt</div>
          <div class="text-h6">{{ fmtEur(summary.dividende) }}</div>
        </v-card>
      </v-col>
      <v-col cols="6" sm="4" md="2">
        <v-card outlined class="pa-3">
          <div class="text-caption text--secondary">Realisiert gesamt</div>
          <div class="text-h6" :class="gainClass(summary.gain)">{{ fmtSignedEur(summary.gain) }}</div>
          <div class="text-caption" :class="gainClass(summary.gainRel)">{{ fmtPct(summary.gainRel) }}</div>
        </v-card>
      </v-col>
      <v-col cols="6" sm="4" md="2">
        <v-card outlined class="pa-3">
          <div class="text-caption text--secondary">Gewinne / Verluste</div>
          <div class="text-h6">
            <span class="green--text text--darken-2">{{ summary.wins }}</span>
            <span class="text--secondary"> / </span>
            <span class="red--text text--darken-2">{{ summary.losses }}</span>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <v-data-table
        :headers="headers"
        :items="sales"
        :items-per-page="-1"
        sort-by="sale_date"
        :sort-desc="true"
        dense
        class="elevation-1"
        no-data-text="Noch keine Verkäufe erfasst"
    >
      <template v-slot:item.name="{ item }">
        <div class="font-weight-medium">{{ item.name || item.isin }}</div>
        <div class="text--secondary text-caption">{{ item.isin }}</div>
      </template>

      <template v-slot:item.sale_date="{ item }">
        {{ fmtDate(item.sale_date) }}
      </template>

      <template v-slot:item.acquisition_date="{ item }">
        {{ fmtDate(item.acquisition_date) }}
      </template>

      <template v-slot:item.pieces="{ item }">
        {{ fmtPieces(item.pieces) }}
      </template>

      <template v-slot:item.price_per_piece="{ item }">
        {{ fmtEur(item.price_per_piece) }}
      </template>

      <template v-slot:item.total_proceeds="{ item }">
        {{ fmtEur(item.total_proceeds) }}
      </template>

      <template v-slot:item.acquisition_cost="{ item }">
        {{ fmtEur(item.acquisition_cost) }}
      </template>

      <template v-slot:item.dividende="{ item }">
        {{ fmtEur(item.dividende) }}
      </template>

      <template v-slot:item.realized_gain_abs="{ item }">
        <span :class="gainClass(item.realized_gain_abs)">
          {{ fmtSignedEur(item.realized_gain_abs) }}
        </span>
      </template>

      <template v-slot:item.realized_gain_rel="{ item }">
        <span :class="gainClass(item.realized_gain_rel)">
          {{ fmtPct(item.realized_gain_rel) }}
        </span>
      </template>
    </v-data-table>
  </v-container>
</template>

<script>
import {fmtDate, fmtEur, fmtSignedEur, fmtPct, fmtPieces, gainClass} from "./finanzenFormat";

export default {
  name: "VerkaeufeView",
  data: () => ({
    headers: [
      {text: "Name / ISIN", value: "name"},
      {text: "Gekauft", value: "acquisition_date"},
      {text: "Verkauft", value: "sale_date"},
      {text: "Stücke", value: "pieces", align: "end"},
      {text: "Kurs", value: "price_per_piece", align: "end"},
      {text: "Erlös", value: "total_proceeds", align: "end"},
      {text: "Anschaffung", value: "acquisition_cost", align: "end"},
      {text: "Dividenden", value: "dividende", align: "end"},
      {text: "Realisiert", value: "realized_gain_abs", align: "end"},
      {text: "%", value: "realized_gain_rel", align: "end"},
    ],
  }),
  computed: {
    sales() {
      return this.$store.state.smarthaus.finanzen.sales
    },
    summary() {
      const sales = this.sales || []
      const num = (v) => {
        const n = Number(v)
        return isNaN(n) ? 0 : n
      }
      let proceeds = 0, cost = 0, dividende = 0, gain = 0, wins = 0, losses = 0
      for (const s of sales) {
        proceeds += num(s.total_proceeds)
        cost += num(s.acquisition_cost)
        dividende += num(s.dividende)
        const g = num(s.realized_gain_abs)
        gain += g
        if (g > 0) wins++
        else if (g < 0) losses++
      }
      return {
        count: sales.length,
        proceeds,
        cost,
        dividende,
        gain,
        gainRel: cost > 0 ? gain / cost : null,
        wins,
        losses,
      }
    },
  },
  methods: {
    fmtDate, fmtEur, fmtSignedEur, fmtPct, fmtPieces, gainClass,
    reload() {
      this.$store.dispatch('update', {"context": '/shares/sales'})
    },
  },
  mounted() {
    this.reload()
  },
}
</script>
